'use client'

import { motion, useInView } from 'framer-motion'
import { useRef } from 'react'
import { RouteLink } from '@/lib/router'
import { siteButtonClass } from './magnetic-button'
import { cn } from '@/lib/utils'
import { RevealText } from './reveal-text'
import { servicePackages } from './capabilities-data'

export function ServicePackages({ id }: { id?: string }) {
  const ref = useRef<HTMLElement>(null)
  const isInView = useInView(ref, { once: true, margin: '-80px' })

  return (
    <section ref={ref} id={id} className="border-t border-border pt-16 md:pt-24">
      <RevealText className="mb-4">
        <span className="text-xs tracking-[0.3em] uppercase text-accent">Investment</span>
      </RevealText>
      <RevealText delay={0.1}>
        <h2 className="mb-4 font-serif text-3xl tracking-tight text-foreground md:text-5xl">
          Service packages
        </h2>
      </RevealText>
      <RevealText delay={0.15}>
        <p className="mb-12 max-w-2xl font-serif text-lg italic text-muted-foreground md:mb-16">
          Transparent starting points - we tailor scope to your business.
        </p>
      </RevealText>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8">
        {servicePackages.map((pkg, index) => (
          <motion.article
            key={pkg.name}
            className={`flex h-full flex-col border bg-card/30 p-8 md:p-10 ${
              'featured' in pkg && pkg.featured
                ? 'border-foreground'
                : 'border-border'
            }`}
            initial={{ opacity: 0, y: 24 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
            transition={{ duration: 0.5, delay: index * 0.08, ease: [0.16, 1, 0.3, 1] }}
          >
            {'featured' in pkg && pkg.featured && (
              <span className="mb-4 inline-block w-fit border border-accent px-3 py-1 text-xs uppercase tracking-[0.2em] text-accent">
                Most popular
              </span>
            )}
            <h3 className="mb-2 font-serif text-2xl tracking-tight text-foreground">{pkg.name}</h3>
            <p className="mb-6 text-sm leading-relaxed text-muted-foreground">{pkg.description}</p>
            <div className="mb-8">
              {pkg.price != null ? (
                <p>
                  <span className="font-serif text-4xl text-foreground">
                    ${pkg.price.toLocaleString()}
                  </span>
                  <span className="ml-2 text-sm text-muted-foreground">one-time</span>
                </p>
              ) : (
                <p className="font-serif text-2xl text-foreground">Custom pricing</p>
              )}
            </div>
            <ul className="mb-10 flex-1 space-y-3">
              {pkg.features.map((feature) => (
                <li key={feature} className="flex items-start gap-3 text-sm text-muted-foreground">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                  {feature}
                </li>
              ))}
            </ul>
            <RouteLink
              to="/contact"
              className={cn(
                siteButtonClass['featured' in pkg && pkg.featured ? 'primary' : 'outline'],
                'block w-full text-center'
              )}
            >
              Get started
            </RouteLink>
          </motion.article>
        ))}
      </div>

      <motion.p
        className="mt-12 text-center text-sm text-muted-foreground"
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 0.5, delay: 0.35 }}
      >
        Need something different?{' '}
        <RouteLink to="/contact" className="text-foreground underline-offset-4 hover:text-accent hover:underline">
          Tell us about your project
        </RouteLink>
      </motion.p>
    </section>
  )
}
